import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  IconBuilding,
  IconChevronLeft,
  IconChevronRight,
  IconCode,
  IconKey,
  IconList,
  IconMenu,
  IconSettings,
  IconX,
} from "../icons";
import { useAuth } from "../../contexts/AuthProvider";
import { useAdminWorkspace } from "../../contexts/AdminWorkspaceProvider";
import { isGlobalManager } from "../../lib/forms/formSource";

const SIDEBAR_COLLAPSED_KEY = "chmforms.admin.sidebar.collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function AdminSidebar({
  collapsed,
  mobileOpen,
  onCollapsedChange,
  onMobileOpenChange,
}: {
  collapsed: boolean;
  mobileOpen: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onMobileOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canManageOrganisations, roles } = useAuth();
  const isAdmin = roles.includes("admin");
  const canViewUnassigned = isGlobalManager(roles);
  const {
    organisations,
    selectedOrganisationId,
    isUnassignedSelected,
    isServiceFormsSelected,
    isLoadingOrganisations,
    selectOrganisation,
    selectServiceForms,
  } = useAdminWorkspace();

  const chooseOrganisation = (organisationId: number | null) => {
    selectOrganisation(organisationId);
    onMobileOpenChange(false);
    navigate("/");
  };

  const desktopLabelClass = collapsed ? "lg:hidden" : "";
  const isFormsRoute =
    location.pathname === "/" || location.pathname.startsWith("/admin/forms/");

  const chooseServiceForms = () => {
    selectServiceForms();
    onMobileOpenChange(false);
    navigate("/");
  };

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Закрыть меню"
          className="fixed inset-0 z-40 bg-slate-950/25 lg:hidden"
          onClick={() => onMobileOpenChange(false)}
        />
      ) : null}

      <aside
        className={[
          "fixed inset-y-0 left-0 z-50 flex border-r border-slate-200 bg-white transition-[width,transform] duration-200",
          "w-64",
          collapsed ? "lg:w-[4.5rem]" : "lg:w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        ].join(" ")}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-16 items-center border-b border-slate-100 px-3">
            <Link
              to="/"
              title="ЦХМ формы"
              className="flex min-w-0 items-center gap-2.5"
              onClick={() => onMobileOpenChange(false)}
            >
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-500 font-unbounded text-sm font-extrabold text-white">
                Ф
              </span>
              <span
                className={`truncate font-unbounded text-sm font-extrabold text-ink ${desktopLabelClass}`}
              >
                ЦХМ формы
              </span>
            </Link>
            <button
              type="button"
              title="Закрыть меню"
              aria-label="Закрыть меню"
              className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 lg:hidden"
              onClick={() => onMobileOpenChange(false)}
            >
              <IconX size={17} />
            </button>
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
            <div>
              <p
                className={`px-3 pb-2 font-onest text-[11px] font-semibold uppercase text-slate-400 ${desktopLabelClass}`}
              >
                Организации
              </p>

              {isLoadingOrganisations ? (
                <div className="space-y-2 px-3 py-1">
                  {[0, 1].map((item) => (
                    <div
                      key={item}
                      className="h-7 animate-pulse rounded bg-slate-100"
                    />
                  ))}
                </div>
              ) : organisations.length === 0 ? (
                <p
                  className={`px-3 py-2 font-onest text-xs text-slate-400 ${desktopLabelClass}`}
                >
                  Нет доступных организаций
                </p>
              ) : (
                <div className="space-y-0.5">
                  {organisations.map((organisation) => {
                    const active =
                      isFormsRoute &&
                      selectedOrganisationId === organisation.id;
                    return (
                      <button
                        key={organisation.id}
                        type="button"
                        title={organisation.name}
                        onClick={() => chooseOrganisation(organisation.id)}
                        className={[
                          "flex min-h-10 w-full items-center gap-3 rounded-md px-3 text-left font-onest text-sm transition-colors",
                          active
                            ? "bg-slate-100 font-medium text-slate-950"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                        ].join(" ")}
                      >
                        <IconBuilding size={18} className="shrink-0" />
                        <span
                          className={`min-w-0 truncate ${desktopLabelClass}`}
                        >
                          {organisation.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {canViewUnassigned || isAdmin ? (
              <div className="mt-5 border-t border-slate-100 pt-3">
                <p
                  className={`px-3 pb-2 font-onest text-[11px] font-semibold uppercase text-slate-400 ${desktopLabelClass}`}
                >
                  Вне организаций
                </p>
                <div className="space-y-0.5">
                  {canViewUnassigned ? (
                    <button
                      type="button"
                      title="Остальные формы"
                      onClick={() => chooseOrganisation(null)}
                      className={[
                        "flex h-10 w-full items-center gap-3 rounded-md px-3 text-left font-onest text-sm transition-colors",
                        isFormsRoute && isUnassignedSelected
                          ? "bg-slate-100 font-medium text-slate-950"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                      ].join(" ")}
                    >
                      <IconList size={18} className="shrink-0" />
                      <span className={desktopLabelClass}>Остальные формы</span>
                    </button>
                  ) : null}

                  {isAdmin ? (
                    <button
                      type="button"
                      title="Сервисные формы"
                      onClick={chooseServiceForms}
                      className={[
                        "flex h-10 w-full items-center gap-3 rounded-md px-3 text-left font-onest text-sm transition-colors",
                        isFormsRoute && isServiceFormsSelected
                          ? "bg-slate-100 font-medium text-slate-950"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                      ].join(" ")}
                    >
                      <IconCode size={18} className="shrink-0" />
                      <span className={desktopLabelClass}>Сервисные формы</span>
                    </button>
                  ) : null}

                  {isAdmin ? (
                    <Link
                      to="/admin/external-services"
                      title="Внешние сервисы"
                      className={[
                        "flex h-10 items-center gap-3 rounded-md px-3 font-onest text-sm transition-colors",
                        location.pathname.startsWith("/admin/external-services")
                          ? "bg-slate-100 font-medium text-slate-950"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                      ].join(" ")}
                      onClick={() => onMobileOpenChange(false)}
                    >
                      <IconKey size={18} className="shrink-0" />
                      <span className={desktopLabelClass}>Внешние сервисы</span>
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : null}
          </nav>

          <div className="border-t border-slate-100 p-2">
            {canManageOrganisations ? (
              <Link
                to="/admin/organisations"
                title="Управление организациями"
                className="flex h-10 items-center gap-3 rounded-md px-3 font-onest text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => onMobileOpenChange(false)}
              >
                <IconSettings size={18} className="shrink-0" />
                <span className={desktopLabelClass}>Настройки организаций</span>
              </Link>
            ) : null}

            <button
              type="button"
              title={collapsed ? "Развернуть панель" : "Свернуть панель"}
              aria-label={collapsed ? "Развернуть панель" : "Свернуть панель"}
              className="mt-1 hidden h-10 w-full items-center gap-3 rounded-md px-3 font-onest text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-900 lg:flex"
              onClick={() => onCollapsedChange(!collapsed)}
            >
              {collapsed ? (
                <IconChevronRight size={18} className="shrink-0" />
              ) : (
                <IconChevronLeft size={18} className="shrink-0" />
              )}
              {!collapsed ? <span>Свернуть</span> : null}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export function AdminMobileHeader({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur lg:hidden">
      <button
        type="button"
        title="Открыть меню"
        aria-label="Открыть меню"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
        onClick={onOpen}
      >
        <IconMenu size={19} />
      </button>
      <Link to="/" className="font-unbounded text-sm font-extrabold text-ink">
        ЦХМ формы
      </Link>
    </div>
  );
}

export function useAdminSidebarState() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // UI state remains available for this page.
    }
  }, [collapsed]);

  return {
    collapsed,
    setCollapsed,
    mobileOpen,
    setMobileOpen,
  };
}
